import React from 'react';
import FormField from '../shared/FormField';
import USStateSelect from '../shared/USStateSelect';
import ZIPCodeInput from '../shared/ZIPCodeInput';
import formDataStorage from '../../utils/formDataStorage';
import { GraduationCap, MapPin, Building, HelpCircle } from 'lucide-react';

// F1签证学校信息表单
const F1SchoolInfoForm = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('F1SchoolInfoForm useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('f1_school_info');
    console.log('F1SchoolInfoForm useEffect - 已保存数据:', savedData);
    console.log('F1SchoolInfoForm useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('F1SchoolInfoForm useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">🎓</span>
          F1签证学校信息
        </h3>

        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          {/* SEVIS ID 部分 */}
          <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <GraduationCap className="w-5 h-5 mr-2" />
              SEVIS 身份证明
            </h4>
            
            <FormField
              label="SEVIS ID"
              value={data.sevis_id || ''}
              onChange={(e) => onChange('sevis_id', e.target.value.slice(0, 11))}
              placeholder="N0123456789"
              maxLength={11}
              required
            />
            
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border-l-2 border-blue-300">
              <p className="text-sm text-blue-700">
                <strong>格式说明：</strong> SEVIS ID 通常以字母开头，后跟10位数字（例如：N0123456789）
              </p>
            </div>
          </div>

          {/* 学校信息部分 */}
          <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-6">
              <Building className="w-5 h-5 mr-2" />
              学校详细信息
            </h4>
            <p className="text-sm text-gray-600 mb-6">
              请提供以下附加信息：
            </p>

            <div className="space-y-6">
              {/* 学校名称 */}
              <FormField
                label="学校名称"
                value={data.school_name || ''}
                onChange={(e) => onChange('school_name', e.target.value.slice(0, 75))}
                placeholder="Stanford University"
                maxLength={75}
                required
              />

              {/* 学习课程 */}
              <FormField
                label="学习课程"
                value={data.course_of_study || ''}
                onChange={(e) => onChange('course_of_study', e.target.value.slice(0, 66))}
                placeholder="Computer Science"
                maxLength={66}
                required
              />

              {/* 地址信息 */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
                <h6 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  学校地址
                </h6>

                <div className="space-y-4">
                  <FormField
                    label="街道地址（第一行）"
                    value={data.school_street_address1 || ''}
                    onChange={(e) => onChange('school_street_address1', e.target.value.slice(0, 40))}
                    placeholder="450 Serra Mall"
                    maxLength={40}
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      街道地址（第二行）<span className="text-gray-500 italic ml-1">*可选</span>
                    </label>
                    <input
                      type="text"
                      value={data.school_street_address2 || ''}
                      onChange={(e) => onChange('school_street_address2', e.target.value.slice(0, 40))}
                      placeholder="Building 50"
                      maxLength={40}
                      className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200"
                    />
                    <p className="text-xs text-gray-500 mt-1">最多40个字符</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="城市"
                      value={data.school_city || ''}
                      onChange={(e) => onChange('school_city', e.target.value.slice(0, 20))}
                      placeholder="Stanford"
                      maxLength={20}
                      required
                    />

                    <USStateSelect
                      label="州"
                      value={data.school_state || ''}
                      onChange={(value) => onChange('school_state', value)}
                      required
                    />
                  </div>

                  <ZIPCodeInput
                    label="邮政编码"
                    value={data.school_zip_code || ''}
                    onChange={(value) => onChange('school_zip_code', value)}
                    placeholder="94305"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 帮助信息 */}
          <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-400">
            <h6 className="text-sm font-semibold text-orange-800 mb-2 flex items-center">
              <HelpCircle className="w-4 h-4 mr-2" />
              填写说明
            </h6>
            <div className="text-sm text-orange-700 space-y-1">
              <p>• <strong>SEVIS ID：</strong> 这是您的学生和交流访问者信息系统标识符，通常以字母开头</p>
              <p>• <strong>学校名称：</strong> 请输入您将要就读的美国学校的完整名称</p>
              <p>• <strong>学习课程：</strong> 对于高中课程，请指明"学术性"或"职业性"；对于其他教育程度，请指明您的主修课程或专业</p>
              <p>• <strong>地址信息：</strong> 请提供学校的完整美国地址信息</p>
              <p>• <strong>字符限制：</strong> 学校名称最多75个字符，学习课程最多66个字符，地址最多40个字符</p>
            </div>
          </div>
        </div>

        {/* 预览信息 */}
        {(data.sevis_id || data.school_name || data.course_of_study) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <h4 className="font-semibold text-blue-800 mb-2">学校信息预览</h4>
            <div className="text-sm text-blue-700 space-y-2">
              {data.sevis_id && (
                <p><strong>SEVIS ID:</strong> {data.sevis_id}</p>
              )}
              {data.school_name && (
                <p><strong>学校名称:</strong> {data.school_name}</p>
              )}
              {data.course_of_study && (
                <p><strong>学习课程:</strong> {data.course_of_study}</p>
              )}
              {(data.school_street_address1 || data.school_city || data.school_state) && (
                <p><strong>地址:</strong> {data.school_street_address1}
                  {data.school_street_address2 && `, ${data.school_street_address2}`}
                  {data.school_city && `, ${data.school_city}`}
                  {data.school_state && `, ${data.school_state}`}
                  {data.school_zip_code && ` ${data.school_zip_code}`}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default F1SchoolInfoForm;
